import './ProductItem.scss';

import { registryComponent } from '@boost-sd/components-registry/registry';
import CustomizableNode from '@components/CustomizableNode';
import { appendClassNames, createClsNameMap, mapModifiers } from '@utils';
import type { PropsWithChildren } from 'react';
import { useEffect, useRef } from 'react';

import type { ProductItemProps } from './types';

const clsNameMap = createClsNameMap({
  modifiers: [
    'no-border',
    'no-padding',
    'border-has-padding',
    'border-no-padding-image',
    'button-with-spacing',
  ],
  elements: {
    info: createClsNameMap(),
    image: createClsNameMap(),
  },
})('product-item');

const ProductItem = ({
  className,
  children,
  borderColor,
  borderColorOnHover,
  backgroundColor,
  backgroundColorOnHover,
  onClick,
  product,
  borderLayout = 'noBorder',
  buttonWithSpacing = false,
  onRender,
  attributes,
}: PropsWithChildren<ProductItemProps>) => {
  const rootElementRef = useRef<HTMLDivElement>(null);
  const originColor = useRef({
    backgroundColor,
    borderColor,
  });

  useEffect(
    function setOriginColor() {
      const rootElement = rootElementRef.current;

      originColor.current.backgroundColor = backgroundColor;
      originColor.current.borderColor = borderColor;

      if (rootElement) {
        originColor.current.backgroundColor =
          getComputedStyle(rootElement).backgroundColor || backgroundColor;
        originColor.current.borderColor = getComputedStyle(rootElement).borderColor || borderColor;
      }
    },
    [backgroundColor, borderColor]
  );

  useEffect(
    function changeBackgroundOnHover() {
      const rootElement = rootElementRef.current;
      if (rootElement) {
        const handler = (event: 'mouseover' | 'mouseout') => {
          if (!backgroundColorOnHover && !borderColorOnHover) return;

          Object.assign(rootElement.style, {
            backgroundColor:
              event === 'mouseover' ? backgroundColorOnHover : originColor.current.backgroundColor,
            borderColor:
              event === 'mouseover' ? borderColorOnHover : originColor.current.borderColor,
          });
        };

        rootElement.addEventListener('mouseover', () => handler('mouseover'));
        rootElement.addEventListener('mouseout', () => handler('mouseout'));

        return () => {
          handler('mouseout');

          rootElement.removeEventListener('mouseover', () => handler('mouseover'));
          rootElement.removeEventListener('mouseout', () => handler('mouseout'));
        };
      }
    },
    [backgroundColorOnHover, borderColorOnHover]
  );

  const style: React.CSSProperties = {
    borderColor: borderColor,
    backgroundColor: backgroundColor,
  };

  return (
    <div
      ref={rootElementRef}
      className={appendClassNames(
        mapModifiers(clsNameMap, {
          'no-border': borderLayout === 'noBorder',
          'no-padding': borderLayout === 'noPadding',
          'border-has-padding': borderLayout === 'hasPadding',
          'border-no-padding-image': borderLayout === 'noPaddingImage',
          'button-with-spacing': buttonWithSpacing === true,
        }),
        className
      )}
      style={style}
      onClick={onClick}
      id={product.id.toString()}
      data-product-id={product?.variant_id?.toString() || product.id.toString()}
      {...attributes}
    >
      <CustomizableNode renderer={onRender}>
        <>{children}</>
      </CustomizableNode>
    </div>
  );
};

export default registryComponent('ProductItem', ProductItem);
